import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import Loading from "../../common/Loading";
import {
  Tooltip,
  Box,
  Button,
  Toolbar,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
} from "@mui/material";

import {
  Search,
  Add,
  Edit,
  Delete,
  Close,
  ArrowBack,
  VpnKey,
  ContentCopy,
  LightbulbOutlined,
  ChevronRight,
  Announcement,
  Block,
} from "@mui/icons-material";
import routes from "../api/routes";
import { messageShow } from "../../common/api/actions";
import { listClientConfig } from "../api/actions/query";
import { deleteClientConfig, registerOAuthClient } from "../api/actions/mutation";
import { checkPermission } from "../../common/Sidebar/checkPermission";
import "./style.css";

const ClientConfig = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const ROWS_PER_PAGE = 10;
  const [permissions, setPermissions] = useState({
    canView: false,
    canEdit: false,
    canDelete: false,
    canCreate: false,
  });
  const [secretData, setSecretData] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
 
  const fetchPermissions = async () => {
    const clientConfigPermissions = await checkPermission("engagement", "crm.clientConfig");
    setPermissions(clientConfigPermissions);
  };

  const fetchClientConfigs = async () => {
    try {
      setLoading(true);
      const { data } = await listClientConfig();
      if (data.success) {
        setConfigs(data.data.configs || []);
      }
    } catch (error) {
      dispatch(messageShow("Failed to load client configurations"));
      console.error("Client config error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
    fetchClientConfigs();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const filtered = configs.filter((item) =>
    (item.clientId || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE);

  const paginated = filtered.slice(
    (page - 1) * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE
  );

  const handleDelete = async (clientConfigId) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete client config?`
    );

    if (!confirmed) return;

    try {
      const { data } = await deleteClientConfig(clientConfigId);

      if (data.success) {
        dispatch(messageShow("Client configuration deleted successfully"));
        fetchClientConfigs();
      }
    } catch (error) {
      console.error("Fialed to delete client config: ", error);
      dispatch(messageShow("Failed to delete client configuration"));
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    fetchClientConfigs();
  };

  const handleRegisterOAuth = async (clientId) => {
    const confirmed = window.confirm(
      `This will regenerate the Auth Key for ${clientId}. Existing services using the old key will fail. Continue?`
    );

    if (!confirmed) return;

    try {
      const { data } = await registerOAuthClient(clientId);
      if (data.success) {
        setSecretData(data.data);
        setOpenDialog(true);
      } else {
        dispatch(messageShow(data.message));
      }
    } catch (error) {
      console.error("Failed to generate OAuth: ", error);
      dispatch(messageShow("Failed to generate OAuth credentials"));
    }
  };

  return (
    <div>
      <Toolbar className="toolbar">
        <IconButton
          color="inherit"
          onClick={() => history.push("/crm")}
          edge="start"
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#0d3559' }}>
          Client Config
        </Typography>
        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title="Spam Reports" arrow>
          <IconButton
            color="error"
            onClick={() => history.push("/messaging/spam-reports")}
            style={{ marginRight: '15px' }}
          >
            <Block />
          </IconButton>
        </Tooltip>

        <Tooltip title="Bounced Emails" arrow>
          <IconButton
            color="warning"
            onClick={() => history.push("/messaging/bounced-email-ids")}
            style={{ marginRight: '15px' }}
          >
            <Announcement />
          </IconButton>
        </Tooltip>

        <Tooltip title="Add Client Config" arrow>
          <Link
            to={{
              pathname: routes.createorUpdateclientConfig.path,
            }}
            style={{
              pointerEvents: permissions?.canCreate ? 'auto' : 'none',
              color: permissions?.canCreate ? 'inherit' : 'grey',
          }}
          >
            <Button startIcon={<Add />} color="primary" size="big" disabled={!permissions?.canCreate}></Button>
          </Link>
        </Tooltip>
      </Toolbar>

      <Box className="tableWrapper">
        <Box className="filterBar">
          <TextField
            variant="outlined"
            placeholder="Search by clientId"
            size="small"
            className="searchInput"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <Close
                    onClick={() => setSearchQuery("")}
                    style={{ cursor: "pointer", fontSize: 18 }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loading ? (
          <Loading />
        ) : (
          <>
            <TableContainer component={Paper} className="tableContainer">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Client ID</TableCell>
                    <TableCell>Channel</TableCell>
                    <TableCell>Key Prefix</TableCell>
                    <TableCell>Folder Path</TableCell>
                    <TableCell width={"20%"}>Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginated.length > 0 ? (
                    paginated.map((row) => (
                      <TableRow key={row._id}>
                        <TableCell>{row.clientId}</TableCell>
                        <TableCell>{row.channel}</TableCell>
                        <TableCell>
                          <code style={{ backgroundColor: '#eee', padding: '2px 4px', borderRadius: '4px' }}>
                            {row.keyPrefix || '---'}
                          </code>
                        </TableCell>
                        <TableCell>{row.folderPath}</TableCell>
                        <TableCell>
                          <Box className="actionGroup">
                            <Link
                              to={{
                                pathname: routes.createorUpdateclientConfig.path,
                                state: { _id: row._id },
                              }}
                              style={{
                                pointerEvents: permissions?.canEdit ? 'auto' : 'none'
                              }}
                            >
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Edit />}
                                className="actionBtn"
                                disabled={!permissions?.canEdit}
                              >
                                Edit
                              </Button>
                            </Link>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Delete />}
                                className="deleteBtn"
                                onClick={() => handleDelete(row._id)}
                                disabled={!permissions?.canDelete}
                              >
                                Delete
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                color="secondary"
                                startIcon={<VpnKey />}
                                className="actionBtn"
                                onClick={() => handleRegisterOAuth(row.clientId)}
                                disabled={!permissions?.canEdit}
                              >
                                Auth Key
                              </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        No client configurations found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Auth Key Generated</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy the Auth Key now. It will NOT be shown again!
          </Alert>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Client ID:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
              {secretData?.clientId}
            </Typography>
            
            <Typography variant="subtitle2">Auth Key:</Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              backgroundColor: '#f5f5f5', 
              p: 1, 
              borderRadius: 1,
              mb: 3
            }}>
              <Typography variant="body1" sx={{ 
                wordBreak: 'break-all',
                fontFamily: 'monospace',
                fontWeight: 'bold',
                flexGrow: 1
              }}>
                {secretData?.authKey}
              </Typography>
              <Tooltip title="Copy to clipboard">
                <IconButton 
                  size="small" 
                  onClick={() => {
                    navigator.clipboard.writeText(secretData?.authKey);
                    dispatch(messageShow("Auth Key copied to clipboard"));
                  }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Usage Tips:</Typography>
            <Box sx={{ backgroundColor: '#e3f2fd', p: 2, borderRadius: 1 }}>
              <Typography variant="caption" display="block" gutterBottom>
                • Add these to your service's <strong>.env</strong> file.
              </Typography>
              <Typography variant="caption" display="block" gutterBottom>
                • Include them in your API headers:
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>
                  x-client-id: {secretData?.clientId}
                </Typography>
                <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>
                  x-auth-key: [Your Auth Key]
                </Typography>
              </Box>
            </Box>
          </Box>

        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary" variant="contained" sx={{ mr: 2, mb: 2 }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ p: 2, pt: 4 }}>
        <Box 
          sx={{ 
            backgroundColor: '#F8F9FE', 
            borderRadius: '8px', 
            border: '1px solid #E9ECEF',
            p: 3
          }}
        >
          <Box display="flex" alignItems="center" mb={2} sx={{ color: '#5E72E4' }}>
            <LightbulbOutlined sx={{ mr: 1, fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              CLIENT CONFIG QUICK TIPS
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="flex-start">
                <ChevronRight sx={{ color: '#5E72E4', fontSize: 20, mt: '2px', mr: 1, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: '#525F7F', lineHeight: 1.6 }}>
                  <span style={{ fontWeight: 600, color: '#32325D' }}>Stateless Authentication:</span> Use <code>x-client-id</code> and <code>x-auth-key</code> in your request headers for service-to-service calls.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="flex-start">
                <ChevronRight sx={{ color: '#5E72E4', fontSize: 20, mt: '2px', mr: 1, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: '#525F7F', lineHeight: 1.6 }}>
                  <span style={{ fontWeight: 600, color: '#32325D' }}>Security Best Practice:</span> Store these keys in your service's <code>.env</code> file. Never expose the full Auth Key in public repositories.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="flex-start">
                <ChevronRight sx={{ color: '#5E72E4', fontSize: 20, mt: '2px', mr: 1, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: '#525F7F', lineHeight: 1.6 }}>
                  <span style={{ fontWeight: 600, color: '#32325D' }}>Key Identification:</span> Use the <code>Key Prefix</code> shown in the table to verify which key is active without needing to regenerate it.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </div>
  );
};

export default connect(null, { messageShow })(ClientConfig);
